// src/App.jsx
import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import AuthGate from "./components/AuthGate";
import RequireAdmin from "./components/RequireAdmin";
import NavBar from "./components/NavBar";

import Dashboard from "./pages/Dashboard";
import FixturePage from "./pages/FixturePage";
import FootballMatchDetail from "./pages/FootballMatchDetail";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import Fixtures from "./pages/Fixtures";
import BetTrackerPage from "./pages/BetTrackerPage";
import PlayerPage from "./pages/PlayerPage";
import SportPlayerPage from "./pages/SportPlayerPage";
import FootballGames from "./pages/FootballGames";
import NflGames from "./pages/NFLGames";
import CfbGames from "./pages/CfbGames";
import CfbFixturePage from "./pages/CfbFixturePage";
import NhlGames from "./pages/NhlGames";
import NhlGameDetail from "./pages/NhlGameDetail";
import Tennis from "./pages/Tennis";
import TennisMatch from "./pages/TennisMatch";
import Basketball from "./pages/Basketball";
import NbaGameDetail from "./pages/NbaGameDetail";
import NflGameDetail from "./pages/NflGameDetail";
import NflTeamPage from "./pages/NflTeamPage";
import NflArchiveGameDetail from "./pages/NflArchiveGameDetail";
import Rugby from "./pages/Rugby";
import RugbyMatchDetail from "./pages/RugbyMatchDetail";
import ClubPage from "./pages/ClubPage";
import LeaguePage from "./pages/LeaguePage";
import WorldCupHub from "./pages/WorldCupHub";
import Baseball from "./pages/Baseball";
import BaseballGameDetail from "./pages/BaseballGameDetail";

import PublicDashboard from "./pages/PublicDashboard";
import Performance from "./pages/Performance";
import OddsMovementPage from "./pages/OddsMovementPage";

import TipstersPage from "./pages/TipstersPage";
import TipsterDetailPage from "./pages/TipsterDetailPage";
import TipsterApply from "./pages/TipsterApply";
import TipsterAddPick from "./pages/TipsterAddPick";
import TipsterAddAcca from "./pages/TipsterAddAcca";
import TipsterEdit from "./pages/TipsterEdit";

import AdminPicks from "./pages/AdminPicks";
import AdminPage from "./pages/AdminPage";
import FootballResearchPage from "./pages/FootballResearchPage";
import AdminTipsterManagement from "./pages/AdminTipsterManagement";
import AdminModeration from "./pages/AdminModeration";

import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import AccountPage from "./pages/AccountPage";
import FollowingFeedPage from "./pages/FollowingFeedPage";
import FollowingLeaderboard from "./pages/FollowingLeaderboard";
import PremiumPage from "./pages/PremiumPage";
import ArbitragePage from "./pages/ArbitragePage";
import UserProfilePage from "./pages/UserProfilePage";
import SignalsPage from "./pages/SignalsPage";

const ForYouPage = lazy(() => import("./pages/ForYouPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const ActivityPage = lazy(() => import("./pages/ActivityPage"));
const PostThreadPage = lazy(() => import("./pages/PostThreadPage"));
const ConnectionsPage = lazy(() => import("./pages/ConnectionsPage"));
const CommunitiesPage = lazy(() => import("./pages/CommunitiesPage"));
const BookmarksPage = lazy(() => import("./pages/BookmarksPage"));
const DailyPlayerPage = lazy(() => import("./pages/DailyPlayerPage"));

function App() {
  return (
    <AuthGate>
      <Router>
        {/* NavBar always visible */}
        <NavBar />

        <main style={{ padding: "20px" }}>
          <Suspense fallback={<div style={{maxWidth:680,margin:"40px auto",color:"#9fb0a6"}}>Loading…</div>}><Routes>
            {/* 🔐 Admin-only protected routes */}
            <Route
              path="/admin"
              element={
                <RequireAdmin>
                  <AdminPage />
                </RequireAdmin>
              }
            />
            <Route
              path="/admin/picks"
              element={
                <RequireAdmin>
                  <AdminPicks />
                </RequireAdmin>
              }
            />
            <Route
              path="/admin/tipsters"
              element={
                <RequireAdmin>
                  <AdminTipsterManagement />
                </RequireAdmin>
              }
            />
            <Route
              path="/admin/moderation"
              element={<RequireAdmin><AdminModeration /></RequireAdmin>}
            />

            <Route path="/admin/football-research" element={<RequireAdmin><FootballResearchPage /></RequireAdmin>} />

            {/* Public dashboard */}
            <Route path="/" element={<PublicDashboard />} />

            {/* Fixture + sports pages */}
            <Route path="/fixture/:id" element={<FootballMatchDetail />} />
            <Route path="/fixture-old/:id" element={<FixturePage />} /> {/* Old version for comparison */}
            <Route path="/club/:teamId/:slug" element={<ClubPage />} />
            <Route path="/league/:leagueCode" element={<LeaguePage />} />
            <Route path="/world-cup" element={<WorldCupHub />} />
            <Route path="/world-cup-2026" element={<WorldCupHub />} />
            <Route path="/world-cup/brackets/:shareCode" element={<WorldCupHub />} />
            <Route path="/fixtures" element={<Fixtures />} />
            <Route path="/bets" element={<BetTrackerPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/football" element={<FootballGames />} />
            <Route path="/player/:id" element={<PlayerPage />} />
            <Route path="/sports/:sport/player/:playerId" element={<SportPlayerPage />} />
            <Route path="/nfl" element={<NflGames />} />
            <Route path="/cfb" element={<CfbGames />} />
            <Route path="/cfb/fixture/:id" element={<CfbFixturePage />} />
            <Route path="/nhl" element={<NhlGames />} />
            <Route path="/nhl/game/:id" element={<NhlGameDetail />} />
            <Route path="/nfl/game/:id" element={<NflGameDetail />} />
            <Route path="/nfl/team/:code" element={<NflTeamPage />} />
            <Route path="/nfl/archive/:season/:gameId" element={<NflArchiveGameDetail />} />
            <Route path="/tennis" element={<Tennis />} />
            <Route path="/tennis/match/:id" element={<TennisMatch />} />
            <Route path="/basketball" element={<Basketball />} />
            <Route path="/basketball/game/:id" element={<NbaGameDetail />} />
            <Route path="/baseball" element={<Baseball />} />
            <Route path="/baseball/game/:id" element={<BaseballGameDetail />} />
            <Route path="/rugby" element={<Rugby />} />
            <Route path="/rugby/match/:id" element={<RugbyMatchDetail />} />
            <Route path="/performance" element={<Performance />} />
            <Route path="/movement" element={<OddsMovementPage />} />

            {/* Tipsters platform */}
            <Route path="/tipsters" element={<TipstersPage />} />
            <Route path="/tipsters/become" element={<TipsterApply />} />
            <Route path="/tipsters/:username" element={<TipsterDetailPage />} />
            <Route path="/tipsters/:username/new-pick" element={<TipsterAddPick />} />
            <Route path="/tipsters/:username/new-acca" element={<TipsterAddAcca />} />
            <Route path="/tipsters/:username/edit" element={<TipsterEdit />} />

            {/* Auth */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />

            {/* Account / social / premium */}
            <Route path="/account" element={<AccountPage />} />
            <Route path="/following" element={<FollowingFeedPage />} />
            <Route path="/for-you" element={<ForYouPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/activity" element={<ActivityPage />} />
            <Route path="/notifications" element={<ActivityPage />} />
            <Route path="/posts/:postId" element={<PostThreadPage />} />
            <Route path="/connections" element={<ConnectionsPage />} />
            <Route path="/communities" element={<CommunitiesPage />} />
            <Route path="/bookmarks" element={<BookmarksPage />} />
            <Route path="/daily-player" element={<DailyPlayerPage />} />
            <Route path="/following/leaderboard" element={<FollowingLeaderboard />} />
            <Route path="/users/:identifier" element={<UserProfilePage />} />
            <Route path="/premium" element={<PremiumPage />} />
            <Route path="/arbitrage" element={<ArbitragePage />} />
            <Route path="/signals" element={<SignalsPage />} />

            {/* Legacy dashboard if you still want it available */}
            <Route path="/legacy-admin" element={<Dashboard />} />
          </Routes></Suspense>
        </main>
      </Router>
    </AuthGate>
  );
}

export default App;
